import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';

export const ERROR_BOUNDARY_NAMES = {
    APP: 'ErrorBoundary',
    LAZY_ROUTE: 'LazyErrorBoundary',
};

const ERROR_BOUNDARY_TAG = 'error-boundary';
const RENDER_ERROR_SUBJECT = 'Render error caught';
const LOG_ORIGIN_LOGGER = 'logger';
const LOG_ORIGIN_NETWORK = 'network';
const LOG_ORIGIN_CONSOLE = 'console';
const MAX_REPEATS_PER_MESSAGE = 5;

// Uncaught-error messages that come from the browser, the network layer or third-party scripts,
// never from our code. Whole-message anchors keep richer messages such as
// "Failed to fetch dynamically imported module" flowing through.
const NOISE_PATTERNS = [
    /^Uncaught "ResizeObserver loop/,
    /^PrebidModule is not loaded/,
    /^Uncaught "Script error\.?"/,
    /^Timeout$/,
    /^Failed to fetch$/,
    /^Load failed$/,
    /^Network Error$/,
    /^(NotReadableError: )?The I\/O read operation failed\.$/,
    /^Fetch is aborted$/,
    /^Uncaught \{"isTrusted":true\}$/,
    /^TCF2 CMP not found\.$/,
    /^Uncaught "Empty reason"$/,
];

export const formatLogMessage = (tag, subject, fields = {}) => {
    const parts = [`[${tag}] ${subject}`];
    Object.entries(fields).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
            parts.push(`${key}=${value}`);
        }
    });
    return parts.join(' | ');
};

export const reportBoundaryError = (error, errorInfo, context = {}) => {
    datadogLogs.logger.error(
        formatLogMessage(ERROR_BOUNDARY_TAG, RENDER_ERROR_SUBJECT, context),
        { ...context, componentStack: errorInfo?.componentStack },
        error,
    );
};

// The logger check runs first so no noise rule can ever drop an error-boundary report.
// Failed fetch/XHR calls are already visible in the API's own logs. Console-origin logs are all
// third-party: the prod bundle strips every app console.* call (Terser drop_console).
// The repeat cap bounds a looping error to a few logs per page load: on release night one
// session sent more than 3,000 identical uncaught errors in a minute.
export const createShouldSendLog = ({ maxRepeatsPerMessage = MAX_REPEATS_PER_MESSAGE } = {}) => {
    const sentCounts = new Map();

    return (log) => {
        if (log.origin === LOG_ORIGIN_LOGGER) {
            return true;
        }
        if (log.origin === LOG_ORIGIN_NETWORK || log.origin === LOG_ORIGIN_CONSOLE) {
            return false;
        }
        if (!log.message) {
            return true;
        }
        if (NOISE_PATTERNS.some((pattern) => pattern.test(log.message))) {
            return false;
        }
        const sent = (sentCounts.get(log.message) || 0) + 1;
        sentCounts.set(log.message, sent);
        return sent <= maxRepeatsPerMessage;
    };
};

export const shouldSendLog = createShouldSendLog();

// Internal member id only: the logging standard forbids email/name in telemetry.
export const syncDatadogUser = (user) => {
    if (user?.id === undefined || user?.id === null) {
        datadogLogs.clearUser();
        datadogRum.clearUser();
        return;
    }
    const datadogUser = { id: String(user.id) };
    datadogLogs.setUser(datadogUser);
    datadogRum.setUser(datadogUser);
};
